import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Building2, Plus, Check, Shield, Loader2, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

import { PLAN_LABELS as PLAN_LABEL } from "@/lib/plans";

export default function Entities() {
  const { session, refresh } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ company_name: "", siren: "", plan_code: "essential" });

  const memberships = session?.memberships || [];
  const currentId = session?.tenant?.id;

  const submit = async () => {
    if (form.company_name.trim().length < 2) {
      toast.error("Nom d'entité obligatoire (min. 2 caractères).");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/me/create-entity", {
        company_name: form.company_name.trim(),
        siren: form.siren.trim() || null,
        plan_code: form.plan_code,
      });
      await refresh();
      toast.success(`Entité « ${r.data.tenant.name} » créée et activée.`);
      setOpen(false);
      setForm({ company_name: "", siren: "", plan_code: "essential" });
      navigate("/app");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.error === "entity_quota_exceeded") {
        // Legitimate quota hit — offer upgrade to Cabinet plan via Stripe.
        if (detail.checkout_url && window.confirm(
          `${detail.message}\n\nVoulez-vous passer au plan Cabinet maintenant ?`
        )) {
          window.location.assign(detail.checkout_url);
          return;
        }
        toast.error(detail.message);
      } else {
        toast.error(apiError(e));
      }
    } finally { setSaving(false); }
  };

  const onSwitch = async (tenant_id) => {
    if (tenant_id === currentId) return;
    try {
      await api.post("/me/switch-tenant", { tenant_id });
      await refresh();
      toast.success("Entité active changée.");
      navigate("/app");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="entities-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
            Mon compte
          </div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Mes entités
          </h1>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            Gérez plusieurs sociétés (holding, filiales) depuis un seul compte.
            Chaque entité est <strong>strictement isolée</strong> — factures,
            trésorerie, membres, audit — comme si c&apos;était des comptes séparés.
            Vous basculez entre entités depuis le sélecteur en haut à gauche.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="create-entity-btn">
          <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} />
          Créer une entité
        </Button>
      </div>

      {/* Isolation banner */}
      <Card className="p-4 border-blue-200 bg-blue-50/50">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" strokeWidth={2} />
          <div className="text-xs text-blue-900">
            <div className="font-medium">Isolation stricte par tenant</div>
            <div className="mt-0.5">
              Créer une nouvelle entité vous en fait automatiquement le dirigeant (rôle owner).
              Les données ne circulent JAMAIS d&apos;une entité vers une autre, y compris pour vous —
              vous voyez les mêmes garde-fous DAL, RBAC, audit et 2FA que si vous aviez créé un compte
              séparé. Le seul lien : la bascule d&apos;une entité à l&apos;autre depuis votre compte utilisateur.
            </div>
          </div>
        </div>
      </Card>

      {/* Entities list */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {memberships.map((m) => (
          <Card key={m.id} className={cn(
            "p-5 flex flex-col justify-between gap-4",
            m.id === currentId ? "border-blue-300 ring-1 ring-blue-100 bg-blue-50/30" : ""
          )} data-testid={`entity-card-${m.id}`}>
            <div>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                  <Building2 className="h-5 w-5 text-slate-500 mt-0.5" strokeWidth={2} />
                  <div>
                    <div className="font-semibold text-slate-900">{m.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                      {m.id.slice(0, 8)}
                    </div>
                  </div>
                </div>
                {m.id === currentId && (
                  <Badge variant="outline"
                         className="text-[10px] border-blue-300 bg-blue-50 text-blue-700">
                    <Check className="h-3 w-3 mr-1" strokeWidth={2} />
                    Active
                  </Badge>
                )}
              </div>
              <div className="mt-3 flex gap-2 text-xs">
                <Badge variant="outline" className="text-[10px]">
                  Plan {PLAN_LABEL[m.plan_code] || m.plan_code}
                </Badge>
                <Badge variant="outline" className="text-[10px] capitalize">
                  {m.role}
                </Badge>
                {m.is_demo && (
                  <Badge variant="outline" className="text-[10px] border-amber-200 bg-amber-50 text-amber-800">
                    Démo
                  </Badge>
                )}
              </div>
            </div>
            {m.id !== currentId && (
              <Button variant="outline" size="sm" onClick={() => onSwitch(m.id)}
                      data-testid={`entity-switch-btn-${m.id}`}
                      className="self-start">
                Basculer sur cette entité
                <ArrowRight className="h-3.5 w-3.5 ml-1" strokeWidth={2} />
              </Button>
            )}
          </Card>
        ))}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="create-entity-dialog">
          <DialogHeader>
            <DialogTitle>Créer une nouvelle entité</DialogTitle>
            <DialogDescription>
              Vous en serez automatiquement le dirigeant. Vos entités actuelles restent inchangées.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Nom de la société <span className="text-red-500">*</span></Label>
              <Input value={form.company_name}
                     onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                     placeholder="Ma Nouvelle Société SAS"
                     data-testid="entity-name-input"
                     className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">SIREN (optionnel)</Label>
              <Input value={form.siren}
                     onChange={(e) => setForm({ ...form, siren: e.target.value })}
                     placeholder="123 456 789"
                     maxLength={20}
                     data-testid="entity-siren-input"
                     className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Plan initial</Label>
              <Select value={form.plan_code}
                       onValueChange={(v) => setForm({ ...form, plan_code: v })}>
                <SelectTrigger className="mt-1" data-testid="entity-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="essential">Essentiel</SelectItem>
                  <SelectItem value="business">Pro</SelectItem>
                  <SelectItem value="scale">Croissance</SelectItem>
                  <SelectItem value="cabinet">Cabinet</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">
                Vous pourrez changer de plan à tout moment via Stripe.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}
                    data-testid="entity-cancel-btn">
              Annuler
            </Button>
            <Button onClick={submit} disabled={saving || !form.company_name.trim()}
                    data-testid="entity-create-btn">
              {saving
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} />}
              Créer l&apos;entité
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
